/**
 * Dashboard WebSocket connection manager.
 *
 * Singleton class that manages a persistent WebSocket connection to the backend
 * dashboard endpoint. Handles authentication, reconnection with exponential
 * backoff, PING/PONG keep-alive, and token refresh forwarding.
 *
 * This is NOT a React hook — it is a standalone module that can be consumed
 * by hooks and other parts of the application.
 */
import { useAuthStore } from '@/stores/auth-store'
import { accessTokenExpiresAt, refreshSession } from './session-refresh'
import type { DashboardEvent, RealtimeOperation } from './ws-types'

export type WsConnectionState =
  | 'connecting'
  | 'connected'
  | 'disconnected'
  | 'reconnecting'

type EventHandler = (event: DashboardEvent) => void
type StateHandler = (state: WsConnectionState) => void
type RealtimeProgressStage = 'accepted' | 'ack' | 'progress'

export type RealtimeProgressState = {
  stage: RealtimeProgressStage
  message?: string
  progress?: number
}

export type RealtimeRequestOptions = {
  timeoutMs?: number
  onProgress?: (state: RealtimeProgressState) => void
}

export type RealtimeDiagnosticResult = {
  ok?: boolean
  status?: string
  message?: string
  template_id?: number
  template_name?: string
  template_job_type?: string
  planned_by?: string
  reason?: string
  symptom?: string
  commands_total?: number
  commands_succeeded?: number
  commands_failed?: number
  results?: Record<string, unknown>
}

export type AWSRDSAccessCheck = {
  key: string
  label: string
  required: boolean
  status: 'passed' | 'failed' | 'unavailable'
  message: string
  reason_code?: string
  remediation?: string
  iam_actions?: string[]
}

export type AWSRDSAccessResult = {
  ok: boolean
  status: 'passed' | 'partial' | 'failed'
  message: string
  provider: 'aws'
  deployment_type: 'aws_rds' | 'aws_aurora'
  checked_at: string
  identity: {
    account_id: string
    principal_arn: string
    role_mode: 'ambient' | 'assume_role'
  } | null
  resource: {
    identifier: string
    region: string
    status?: string | null
    engine?: string | null
    instance_class?: string | null
    multi_az?: boolean | null
    publicly_accessible?: boolean | null
    storage_encrypted?: boolean | null
  } | null
  summary: {
    passed: number
    required_failed: number
    optional_unavailable: number
    total: number
  }
  checks: AWSRDSAccessCheck[]
}

type PendingRealtimeRequest = {
  resolve: (value: unknown) => void
  reject: (error: Error) => void
  onProgress?: (state: RealtimeProgressState) => void
  timeoutTimer: ReturnType<typeof setTimeout>
}

const WS_PATH = '/api/v1/dashboard/ws'
const PING_INTERVAL_MS = 30_000
const INITIAL_RECONNECT_DELAY_MS = 1_000
const MAX_RECONNECT_DELAY_MS = 30_000
const MAX_RECONNECT_ATTEMPTS = 8
const RENEW_BEFORE_MS = 30_000
const DEFAULT_RT_TIMEOUT_MS = 45_000

export class DashboardWebSocket {
  private ws: WebSocket | null = null
  private reconnectDelay = INITIAL_RECONNECT_DELAY_MS
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private pingTimer: ReturnType<typeof setInterval> | null = null
  private eventHandlers = new Set<EventHandler>()
  private stateHandlers = new Set<StateHandler>()
  private pendingRealtimeRequests = new Map<string, PendingRealtimeRequest>()
  private _state: WsConnectionState = 'disconnected'
  private intentionalClose = false
  private epoch = 0
  private sessionVersion = -1
  private preparing = false
  private reconnectAttempts = 0
  private expiryTimer: ReturnType<typeof setTimeout> | null = null
  private rejectedToken: string | null = null
  private socketToken: string | null = null
  private renewalAttempts = 0

  get state(): WsConnectionState {
    return this._state
  }

  connect(): void {
    if (this.preparing) return
    if (this.ws?.readyState === WebSocket.OPEN) return
    if (this.ws?.readyState === WebSocket.CONNECTING) return

    const { accessToken, sessionVersion } = useAuthStore.getState().auth
    if (!accessToken) return

    this.intentionalClose = false
    this.clearReconnectTimer()
    this.sessionVersion = sessionVersion
    const epoch = ++this.epoch
    this.preparing = true
    if (this._state !== 'reconnecting') this.setState('connecting')
    void this.prepareConnection(accessToken, epoch)
  }

  private owns(epoch: number): boolean {
    return (
      !this.intentionalClose &&
      this.epoch === epoch &&
      useAuthStore.getState().auth.sessionVersion === this.sessionVersion
    )
  }

  private async prepareConnection(token: string, epoch: number): Promise<void> {
    try {
      const expiry = accessTokenExpiresAt(token)
      if (
        (expiry !== null && expiry <= Date.now() + RENEW_BEFORE_MS) ||
        token === this.rejectedToken
      ) {
        token = await refreshSession()
      }
      if (!this.owns(epoch)) return
      // HTTP renewal may have completed while this connection was preparing.
      token = useAuthStore.getState().auth.accessToken ?? token
      if (token === this.rejectedToken) {
        this.setState('disconnected')
        return
      }
      this.openSocket(token, epoch)
    } catch {
      if (this.owns(epoch)) {
        this.setState('reconnecting')
        this.scheduleReconnect()
      }
    } finally {
      if (this.epoch === epoch) this.preparing = false
    }
  }

  private openSocket(accessToken: string, epoch: number): void {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const host = window.location.host
    const url = `${protocol}//${host}${WS_PATH}?token=${encodeURIComponent(accessToken)}`

    let socket: WebSocket
    try {
      socket = new WebSocket(url)
    } catch {
      this.setState('reconnecting')
      this.scheduleReconnect()
      return
    }
    this.ws = socket
    this.socketToken = accessToken
    const ownsSocket = () => this.owns(epoch) && this.ws === socket

    socket.onopen = () => {
      if (!ownsSocket()) return
      this.startPing()
      const currentToken = useAuthStore.getState().auth.accessToken
      if (currentToken && currentToken !== accessToken) {
        this.sendTokenRefresh(currentToken)
      } else {
        this.scheduleRenewal(accessToken, epoch)
      }
    }

    socket.onmessage = (event) => {
      if (!ownsSocket()) return
      try {
        const data = JSON.parse(event.data) as DashboardEvent
        if (data.type === 'CONNECTED') {
          this.setState('connected')
          this.reconnectDelay = INITIAL_RECONNECT_DELAY_MS
          this.reconnectAttempts = 0
          this.rejectedToken = null
          return
        }
        if (data.type === 'ERROR') {
          // The dashboard protocol reserves ERROR for session authentication.
          this.rejectedToken = this.socketToken
          socket.close()
          return
        }
        if (this.handleRealtimeEvent(data)) return
        // Filter out internal messages
        if (data.type === 'PONG') return
        this.eventHandlers.forEach((handler) => handler(data))
      } catch {
        // Ignore malformed messages
      }
    }

    socket.onclose = (event) => {
      if (!ownsSocket()) return
      this.ws = null
      this.stopPing()
      this.clearExpiryTimer()
      this.rejectAllPendingRealtimeRequests('Realtime connection closed')
      if (event.code === 1008) {
        this.rejectedToken = this.socketToken
      }
      if (!this.intentionalClose) {
        this.setState('reconnecting')
        this.scheduleReconnect()
      } else {
        this.setState('disconnected')
      }
    }

    socket.onerror = () => {
      // onclose will fire after onerror, so we handle cleanup there
    }
  }

  disconnect(): void {
    this.intentionalClose = true
    this.epoch++
    this.preparing = false
    this.reconnectAttempts = 0
    this.reconnectDelay = INITIAL_RECONNECT_DELAY_MS
    this.rejectedToken = null
    this.socketToken = null
    this.renewalAttempts = 0
    this.clearReconnectTimer()
    this.clearExpiryTimer()
    this.stopPing()
    this.rejectAllPendingRealtimeRequests('Realtime connection closed')
    if (this.ws) {
      this.ws.onopen = null
      this.ws.onclose = null
      this.ws.onerror = null
      this.ws.onmessage = null
      this.ws.close()
      this.ws = null
    }
    this.setState('disconnected')
  }

  /** Forward a refreshed JWT to keep the connection authenticated. */
  sendTokenRefresh(newToken: string): void {
    if (
      !this.owns(this.epoch) ||
      useAuthStore.getState().auth.accessToken !== newToken
    )
      return
    if (
      this.ws?.readyState === WebSocket.OPEN &&
      this.socketToken !== newToken
    ) {
      try {
        this.ws.send(JSON.stringify({ type: 'TOKEN_REFRESH', token: newToken }))
        this.socketToken = newToken
        this.renewalAttempts = 0
        this.scheduleRenewal(newToken, this.epoch)
      } catch {
        // The close handler owns reconnect; never replay pending operations.
      }
    }
  }

  private clearExpiryTimer(): void {
    if (this.expiryTimer) clearTimeout(this.expiryTimer)
    this.expiryTimer = null
  }

  private scheduleRenewal(token: string, epoch: number): void {
    this.clearExpiryTimer()
    const expiry = accessTokenExpiresAt(token)
    if (expiry === null) return
    // Invalid/very short server tokens must not create immediate renewal loops.
    const delay = Math.max(
      5_000,
      Math.min(expiry - Date.now() - RENEW_BEFORE_MS, 2_147_483_647)
    )
    this.expiryTimer = setTimeout(() => {
      if (!this.owns(epoch)) return
      void refreshSession()
        .then((nextToken) => {
          if (this.owns(epoch)) this.sendTokenRefresh(nextToken)
        })
        .catch(() => {
          if (!this.owns(epoch)) return
          if (++this.renewalAttempts >= MAX_RECONNECT_ATTEMPTS) {
            this.disconnect()
          } else {
            this.scheduleRenewal(token, epoch)
          }
        })
    }, delay)
  }

  requestRealtime<T>(
    operation: RealtimeOperation,
    instanceId: number,
    payload: Record<string, unknown> = {},
    options: RealtimeRequestOptions = {}
  ): Promise<T> {
    if (
      !this.owns(this.epoch) ||
      this._state !== 'connected' ||
      this.ws?.readyState !== WebSocket.OPEN
    ) {
      return Promise.reject(new Error('Realtime channel is not connected'))
    }

    const requestId = this.generateRequestId()
    const timeoutMs = options.timeoutMs ?? DEFAULT_RT_TIMEOUT_MS

    return new Promise<T>((resolve, reject) => {
      const timeoutTimer = setTimeout(() => {
        this.pendingRealtimeRequests.delete(requestId)
        reject(new Error('Realtime request timed out'))
      }, timeoutMs)

      this.pendingRealtimeRequests.set(requestId, {
        resolve: resolve as (value: unknown) => void,
        reject,
        onProgress: options.onProgress,
        timeoutTimer,
      })

      try {
        this.ws?.send(
          JSON.stringify({
            type: 'RT_REQUEST',
            request_id: requestId,
            op: operation,
            instance_id: instanceId,
            payload,
          })
        )
      } catch (error) {
        const pending = this.pendingRealtimeRequests.get(requestId)
        if (pending) {
          clearTimeout(pending.timeoutTimer)
          this.pendingRealtimeRequests.delete(requestId)
        }
        reject(
          error instanceof Error
            ? error
            : new Error('Failed to send realtime request')
        )
      }
    })
  }

  requestConnectionTest(
    instanceId: number,
    options: RealtimeRequestOptions = {}
  ): Promise<{
    ok: boolean
    message?: string
    details?: Record<string, unknown>
  }> {
    return this.requestRealtime('connection.test', instanceId, {}, options)
  }

  requestAWSRDSAccessTest(
    instanceId: number,
    options: RealtimeRequestOptions = {}
  ): Promise<AWSRDSAccessResult> {
    return this.requestRealtime('aws.rds.access.test', instanceId, {}, options)
  }

  requestTriageQuick(
    instanceId: number,
    symptom: string,
    options: RealtimeRequestOptions = {}
  ): Promise<RealtimeDiagnosticResult> {
    return this.requestRealtime(
      'triage.quick',
      instanceId,
      { symptom },
      options
    )
  }

  requestTemplateExecution(
    instanceId: number,
    payload: {
      templateId: number
      parameters?: Record<string, unknown>
      confirmation?: string
      label?: string
    },
    options: RealtimeRequestOptions = {}
  ): Promise<RealtimeDiagnosticResult> {
    return this.requestRealtime(
      'template.execute',
      instanceId,
      {
        template_id: payload.templateId,
        parameters: payload.parameters,
        confirmation: payload.confirmation,
        label: payload.label,
      },
      options
    )
  }

  onEvent(handler: EventHandler): () => void {
    this.eventHandlers.add(handler)
    return () => {
      this.eventHandlers.delete(handler)
    }
  }

  onStateChange(handler: StateHandler): () => void {
    this.stateHandlers.add(handler)
    return () => {
      this.stateHandlers.delete(handler)
    }
  }

  private setState(state: WsConnectionState): void {
    this._state = state
    this.stateHandlers.forEach((h) => h(state))
  }

  private startPing(): void {
    this.stopPing()
    this.pingTimer = setInterval(() => {
      if (this.owns(this.epoch) && this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'PING' }))
      }
    }, PING_INTERVAL_MS)
  }

  private stopPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer)
      this.pingTimer = null
    }
  }

  private scheduleReconnect(): void {
    this.clearReconnectTimer()
    if (++this.reconnectAttempts > MAX_RECONNECT_ATTEMPTS) {
      this.setState('disconnected')
      return
    }
    const epoch = this.epoch
    this.reconnectTimer = setTimeout(() => {
      if (!this.owns(epoch)) return
      this.reconnectDelay = Math.min(
        this.reconnectDelay * 2,
        MAX_RECONNECT_DELAY_MS
      )
      this.connect()
    }, this.reconnectDelay)
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

  private handleRealtimeEvent(event: DashboardEvent): boolean {
    const payload = (event.payload ?? {}) as Record<string, unknown>
    const requestId =
      typeof payload.request_id === 'string' ? payload.request_id : undefined

    if (event.type === 'RT_REQUEST_ACCEPTED') {
      if (!requestId) return true
      const pending = this.pendingRealtimeRequests.get(requestId)
      pending?.onProgress?.({
        stage: 'accepted',
        message:
          typeof payload.message === 'string'
            ? payload.message
            : 'Request accepted by backend',
      })
      return true
    }

    if (event.type === 'RT_ACK' || event.type === 'RT_PROGRESS') {
      if (!requestId) return true
      const pending = this.pendingRealtimeRequests.get(requestId)
      pending?.onProgress?.({
        stage: event.type === 'RT_ACK' ? 'ack' : 'progress',
        message:
          typeof payload.message === 'string' ? payload.message : undefined,
        progress:
          typeof payload.progress === 'number' ? payload.progress : undefined,
      })
      return true
    }

    if (event.type === 'RT_COMPLETE') {
      if (!requestId) return true
      const pending = this.pendingRealtimeRequests.get(requestId)
      if (!pending) return true
      clearTimeout(pending.timeoutTimer)
      this.pendingRealtimeRequests.delete(requestId)
      const result = payload.result ?? payload
      pending.resolve(result)
      return true
    }

    if (event.type === 'RT_ERROR') {
      if (!requestId) return false
      const pending = this.pendingRealtimeRequests.get(requestId)
      if (!pending) return false
      clearTimeout(pending.timeoutTimer)
      this.pendingRealtimeRequests.delete(requestId)
      const code = typeof payload.code === 'string' ? payload.code : 'RT_ERROR'
      const message =
        typeof payload.message === 'string'
          ? payload.message
          : 'Realtime request failed'
      pending.reject(new Error(`${code}: ${message}`))
      return true
    }

    return false
  }

  private rejectAllPendingRealtimeRequests(reason: string): void {
    this.pendingRealtimeRequests.forEach((pending) => {
      clearTimeout(pending.timeoutTimer)
      pending.reject(new Error(reason))
    })
    this.pendingRealtimeRequests.clear()
  }

  private generateRequestId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID()
    }
    return `rt-${Date.now()}-${Math.random().toString(16).slice(2)}`
  }
}

// Singleton instance
export const dashboardWs = new DashboardWebSocket()
